import { useState, useEffect, useRef } from 'react';
import { featureService } from '../services/api';

export function useFeatureFlag(featureName) {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function checkFeature() {
      setIsLoading(true);
      try {
        const enabled = await featureService.isFeatureEnabled(featureName);
        setIsEnabled(enabled);
      } catch (error) {
        console.error(`Error checking feature flag ${featureName}:`, error);
        setIsEnabled(false);
      } finally {
        setIsLoading(false);
      }
    }

    checkFeature();
  }, [featureName]);

  return { isEnabled, isLoading };
}

export function useFeatureFlags(featureNames) {
  const [features, setFeatures] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const prevFeatureNamesRef = useRef();

  useEffect(() => {
    // Check if featureNames actually changed
    const featureNamesStr = JSON.stringify(featureNames);
    if (prevFeatureNamesRef.current === featureNamesStr) {
      return; // No change, skip the effect
    }
    prevFeatureNamesRef.current = featureNamesStr;

    async function checkFeatures() {
      setIsLoading(true);
      try {
        const allFeatures = await featureService.getAllFeatures();
        const requestedFeatures = {};
        featureNames.forEach(name => {
          requestedFeatures[name] = allFeatures[name] || false;
        });
        setFeatures(requestedFeatures);
      } catch (error) {
        console.error('Error checking feature flags:', error);
        const defaultFeatures = {};
        featureNames.forEach(name => {
          defaultFeatures[name] = false;
        });
        setFeatures(defaultFeatures);
      } finally {
        setIsLoading(false);
      }
    }

    checkFeatures();
  }, [featureNames]); // Now safe to use the array directly

  return { features, isLoading };
}

export function useAllFeatures() {
  const [features, setFeatures] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadFeatures() {
      setIsLoading(true);
      try {
        const allFeatures = await featureService.getAllFeatures();
        setFeatures(allFeatures);
      } catch (error) {
        console.error('Error loading all features:', error);
        setFeatures({});
      } finally {
        setIsLoading(false);
      }
    }

    loadFeatures();
  }, []);

  return { features, isLoading };
}
